import React, { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Grid, Modal } from '~/components';
import FormGroup from '~/components/form/form-group';
import FormSelect from '~/components/form/form-select';
import { addressDetails } from '~/api';

const headerClassName = 'py-1 px-2 border-b text-left';
const cellProps = {
    className: 'py-1 px-2'
};

const modalcolumns = [
    { 
        title: 'City', 
        field: 'City', 
        headerClassName, 
        props: cellProps 
    },
    { 
        title: 'County', 
        field: 'County', 
        headerClassName, 
        props: cellProps 
    },
    { 
        title: 'State', 
        field: 'State', 
        headerClassName, 
        props: cellProps 
    }
];

export const PropertyAddressForm = ({ state = {}, fieldActions = {}, readonly = false, setLoader, indicateWhenFieldsAreRequired=true }) => {

    const { stateOptions } = state.values;
    const [isOpen, setOpen] = useState(false);
    const [addressOptions, setAddressOptions] = useState([]);
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);

    const notEmpty = val => {
        if (!indicateWhenFieldsAreRequired) {
            return true;
        }
        return /(.|\s)*\S(.|\s)*/.test(val) ? true : 'This is a required field';
    };

    return (
        <>
            <FormGroup
                label="Street No"
                required={indicateWhenFieldsAreRequired}
                readOnly={readonly}
                groupClassName="col-span-1 col-start-1"
                path='streetNumber'
                value={state.values.streetNumber}
                error={state.errors.streetNumber}
                {...fieldActions}
            />
            <FormGroup
                label="Street Name"
                required={indicateWhenFieldsAreRequired}
                readOnly={readonly}
                groupClassName="col-span-3"
                path='streetName'
                value={state.values.streetName}
                error={state.errors.streetName}
                {...fieldActions}
                validator={notEmpty}
            />
            <FormGroup
                label="Street Suffix"
                readOnly={readonly}
                groupClassName="col-span-2"
                path='streetSuffix'
                value={state.values.streetSuffix}
                error={state.errors.streetSuffix}
                {...fieldActions}
            />
            <FormGroup
                label="ZIP Code"
                required={indicateWhenFieldsAreRequired}
                readOnly={readonly}
                groupClassName="col-span-2"
                path='zipcode'
                value={state.values.zipcode}
                error={state.errors.zipcode}
                {...fieldActions}
                validator={value => /^[0-9]{5}(?:-[0-9]{4})?$|^$/.test(value) ? true : 'Invalid zip code'}
                onBlur={async (value, path, validator) => {
                    const zipValidation = validator(value);

                    if (zipValidation !== true) {
                        fieldActions.onBlur(value, path, validator);
                    } else {
                        fieldActions.onBlur(value, path, validator);

                        setLoader(true);
                        await addressDetails.getAddressDetails(value)
                            .then(results => {
                                if (results === null)
                                    return;
                                if (results.length > 1) {
                                    setAddressOptions(results);
                                    setOpen(true);
                                } else if (results.length == 1) {
                                    const { City: city, State: stateCode, County: county } = results[0];
                                    const stateOption = stateOptions.find(s => s.value == stateCode);
                                    
                                    fieldActions.onChange(city, 'city');
                                    fieldActions.onChange(stateOption, 'state');
                                    fieldActions.onChange(county, 'county');
                                }

                                setLoader(false);
                            });
                    }
                }}
            />
            <FormGroup
                label="City"
                required={indicateWhenFieldsAreRequired}
                readOnly={readonly}
                groupClassName="col-span-2"
                path='city'
                value={state.values.city}
                error={state.errors.city}
                {...fieldActions}
            />
            <FormSelect
                label="State"
                type='select'
                required={indicateWhenFieldsAreRequired}
                readOnly={readonly}
                isSearchable
                isClearable
                options={stateOptions}
                groupClassName="col-span-2"
                path='state'
                value={state.values.state}
                error={state.errors.state}
                {...fieldActions}
            />
            <FormGroup
                label="County"
                required={indicateWhenFieldsAreRequired}
                readOnly={readonly}
                groupClassName="col-span-2"
                path='county'
                value={state.values.county}
                error={state.errors.county}
                {...fieldActions}
                validator={notEmpty}
            />
            <Modal isModalOpen={isOpen} preventScroll className='w-1/2 rounded-md bg-white'>
                <section className='w-full h-full flex flex-col items-stretch justify-start'>
                    <header className="border-b-2 text-sm flex items-center justify-between py-2 px-6">
                        <p className='font-semibold text-base'>Select an Address</p>
                        <button 
                            className="py-2 rounded-full"
                            onClick={() => setOpen(false)}
                        >
                            <MdClose size="1em" />
                        </button>
                    </header>
                    <div className='p-4'>
                        <p className='mb-4 px-2'>
                            There are multiple cities and/or counties associated with this zipcode.
                            Please select one below.
                        </p>
                        <Grid
                            rowSelect 
                            data={addressOptions} 
                            columns={modalcolumns}
                            events={{
                                onRowClick: (_, index) => {
                                    if (selectedAddressIndex != index) {
                                        setSelectedAddressIndex(index);
                                    } else {
                                        setSelectedAddressIndex(-1);
                                    }
                                }
                            }}/>
                        <div className='p-4 flex items-center justify-end h-24'>
                            {selectedAddressIndex >= 0 && (
                                <button
                                    className='py-2 px-4 rounded-full text-white bg-primary-60 hover:bg-primary-50 focus:outline-none'
                                    onClick={() => {
                                        const selected = addressOptions[selectedAddressIndex];
                                        const { City: city, State: stateCode, County: county } = selected;
                                        const stateOption = stateOptions.find(s => s.value == stateCode);
                                        
                                        fieldActions.onChange(city, 'city');
                                        fieldActions.onChange(stateOption, 'state');
                                        fieldActions.onChange(county, 'county');
                                        setOpen(false);
                                        setAddressOptions([]);
                                        setSelectedAddressIndex(-1);
                                    }}>
                                    Confirm
                                </button>
                            )}
                            <button
                                className='ml-2 py-2 px-4 border rounded-full bg-secondary-10 hover:bg-secondary-30 hover:border-secondary-30 focus:outline-none'
                                onClick={() => {
                                    setSelectedAddressIndex(-1);
                                    fieldActions.onChange('', 'zipcode');
                                    setOpen(false);
                                }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </section>
            </Modal>
        </>
    );
};

export default PropertyAddressForm;